/**
 * Wire shape of every frame the assistant WebSocket sends in either
 * direction. Lives in `@orn/global` so the server-side dispatch
 * (`@orn/api`'s `assistantWebSocket.ts` + `AssistantOrchestrator.ts`) and
 * the client-side consumer (`@orn/web`'s `useAssistantWebSocket.ts`) share
 * one source of truth — drift between the two would be a silent protocol
 * bug, exactly the class of failure `@orn/global` exists to prevent.
 *
 * Symbols that depend on `@earendil-works/pi-agent-core` (the orchestrator's
 * custom message kinds — `system_observation`, `system_instruction`, the
 * `CustomAgentMessages` declaration-merge, `convertToLlm`) stay in
 * `@orn/api`'s `assistant/types.ts` because `@orn/global` is a leaf
 * package that cannot import framework-coupled types. The api-side file
 * re-exports everything here so existing api callers keep their
 * `./types.js` import path.
 *
 * Wire conventions:
 *
 *  - **One LLM round per `IAssistantMessage` frame.** The orchestrator
 *    emits one `assistant_message` per round whose assistant message
 *    carried any non-empty text. A multi-round turn (the model writing
 *    prose between tool calls, or the proactive bootstrap greeting first
 *    and then surfacing its top item once its lookups come back) produces
 *    N `assistant_message` frames each rendered as its own bubble.
 *  - **`assistant_message_delta` frames stream a round's text live.** As
 *    the model produces prose the orchestrator emits each text slice as
 *    its own {@link IAssistantMessageDelta}; the client grows a streaming
 *    bubble and the round's terminal `assistant_message` snaps it to the
 *    authoritative full text. Deltas are wire-only ephemera — never
 *    persisted, and a delta-unaware client still renders correctly off
 *    the `assistant_message` frame alone.
 *  - **Exactly one `ITurnComplete` per turn.** Terminal frame for the
 *    turn. Carries the echo of every originating bubble's
 *    `clientMessageId` so the client flips matching pending bubbles to
 *    confirmed in a single pass. Server-initiated turns (today: the
 *    proactive session bootstrap) omit `clientMessageIds` — there are no
 *    pending bubbles to confirm, but the client still uses the frame as
 *    the queue-drain signal.
 *  - **`status` ('thinking' / 'complete') wraps every turn.** Sent before
 *    the first round and after the last, plus the bootstrap pre-emit.
 *  - **`tool_started` / `tool_completed`** carry tool detail (name / args /
 *    output) only for viewers passing the `debug-assistant` gate; the client
 *    logs them to the console as they complete and renders no tool UI.
 *
 * Discriminant string literals are exported as `as const` constants
 * (`SERVER_MESSAGE_TYPE` / `CLIENT_MESSAGE_TYPE`) so neither side
 * type-checks against magic strings.
 *
 * Wire shape grows additively: new optional fields on existing variants,
 * or new variants on the union (never breaking changes to existing field
 * shapes). The variant-presence test in `wireProtocol.unit.test.ts` is
 * the regression catch for accidental shrinkage.
 */

import type { IObservationDocument, SnapshotMessage } from './snapshotMessage.js';
import type { IWorkItemMoveFacts } from './workItemMove.js';

// ============================================
// Discriminant string literals (single source)
// ============================================

/**
 * Type discriminator strings for every {@link ServerMessage} variant.
 * Importers reference `SERVER_MESSAGE_TYPE.AssistantMessage` instead of
 * the bare string `'assistant_message'` so a renamed variant trips a
 * TypeScript error at every call site instead of a silent runtime drift.
 */
export const SERVER_MESSAGE_TYPE = {
  Status: 'status',
  ToolStarted: 'tool_started',
  ToolCompleted: 'tool_completed',
  AssistantMessageDelta: 'assistant_message_delta',
  AssistantMessage: 'assistant_message',
  TurnComplete: 'turn_complete',
  Error: 'error',
  TranscriptSnapshot: 'transcript_snapshot',
  AssistantReady: 'assistant_ready',
  ThreadReset: 'thread_reset',
  InvokeClientTool: 'invoke_client_tool',
  CancelClientTool: 'cancel_client_tool',
  DataChanged: 'data_changed',
  AlertCard: 'alert_card',
} as const;

/**
 * Type discriminator strings for every {@link ClientMessage} variant.
 * Same identity-rather-than-string-literal contract as
 * {@link SERVER_MESSAGE_TYPE}.
 */
export const CLIENT_MESSAGE_TYPE = {
  UserMessage: 'user_message',
  AlertExpand: 'alert_expand',
  StopTurn: 'stop_turn',
  ClientToolResult: 'client_tool_result',
} as const;

/**
 * The client frames that open a turn, as opposed to the control frames the
 * server answers out-of-band. Both are *serialized behind any in-flight turn*
 * and both are rejected before the {@link IAssistantReady} handshake, so the
 * two gates in `assistantWebSocket` read this set rather than naming
 * `user_message` — a new turn frame that forgets either gate reaches an
 * unbound orchestrator, which fails as a data-integrity bug rather than a
 * visible one.
 */
export const TURN_FRAME_TYPES: ReadonlySet<string> = new Set([
  CLIENT_MESSAGE_TYPE.UserMessage,
  CLIENT_MESSAGE_TYPE.AlertExpand,
]);

// ============================================
// Client → Server
// ============================================

/**
 * One element of {@link IUserMessageRequest}.messages — a single
 * optimistic-bubble part. Shared with the orchestrator and the
 * persistence layer so the inline shape isn't redeclared at every call
 * site.
 *
 * `attachmentDocumentIds` is per-bubble: when present, the orchestrator
 * pushes one `system_observation` row into agent state immediately
 * before the corresponding user row so the LLM sees the attachment
 * context paired with the bubble that owns it. Empty / omitted means
 * "no attachments on this bubble" — the orchestrator skips the
 * observation push.
 */
export type UserMessagePart = {
  content: string;
  clientMessageId?: string;
  attachmentDocumentIds?: string[];
};

/**
 * Client → server "user said this" frame. Carries N ≥ 1 optimistic
 * bubbles in a single envelope. N = 1 is a single-message turn; N ≥ 2
 * fires when the web client flushes a batched queue (the user typed
 * multiple sends while the WS was reconnecting or while the previous
 * turn was still processing — see `useAssistantWebSocket.flushPendingQueue`).
 *
 * The server runs ONE LLM call per frame regardless of N, persists N
 * user rows (one per bubble) plus any `system_observation` rows the
 * bubbles' `attachmentDocumentIds` produce, and echoes every populated
 * `clientMessageId` back on the terminal {@link ITurnComplete} frame's
 * `clientMessageIds` so the client can confirm all N pending bubbles in
 * a single pass.
 *
 * `messages` must be non-empty — the server rejects empty arrays with
 * `EMPTY_USER_MESSAGE`. `clientMessageId` and `attachmentDocumentIds`
 * are both optional on each entry; the composer populates them
 * per-bubble.
 */
export interface IUserMessageRequest {
  type: typeof CLIENT_MESSAGE_TYPE.UserMessage;
  messages: UserMessagePart[];
}

/**
 * Client → server "the user opened this alert" frame — sent when a row in the
 * composer's alert strip is clicked. The server resolves the id against the
 * viewer's own alerts and runs one turn about it, so the reply is grounded in
 * the row the user actually pointed at.
 *
 * A *turn frame* like {@link IUserMessageRequest}, serialized behind any
 * in-flight turn: an expand starts and finishes a turn, which is exactly what
 * a control frame may not do. It carries no prose — the id is the whole
 * payload, and the server never interpolates the client's string into the
 * turn it starts (see `handleAlertExpand`).
 *
 * Deliberately not a `user_message` with synthesized text: the user did not
 * type anything, and a fabricated bubble would sit in their history forever
 * as words they never said.
 */
export interface IAlertExpandRequest {
  type: typeof CLIENT_MESSAGE_TYPE.AlertExpand;
  alertId: string;
}

/**
 * Client → server "stop the in-flight turn" control frame. Sent when the user
 * clicks the composer's stop button while a turn's agent run is live.
 *
 * Unlike {@link IUserMessageRequest} (a *turn frame*, serialized behind any
 * in-flight turn), this is a *control frame*: the server handles it
 * out-of-band — immediately, NOT chained onto the per-socket turn-processing
 * queue — because chaining it behind the very turn it means to interrupt would
 * never let it run until that turn finished. It is idempotent and
 * side-effect-only: it aborts the active agent run if one exists and otherwise
 * does nothing (a stop with no active run — duplicate clicks, a stop landing
 * between turns or during synchronous compaction — is a safe no-op). It never
 * starts or completes a turn; the turn it aborts emits its own terminal
 * {@link ITurnComplete}.
 */
export interface IStopTurnRequest {
  type: typeof CLIENT_MESSAGE_TYPE.StopTurn;
}

/**
 * Client → server result of a {@link IInvokeClientTool} round-trip — the
 * second half of a client-exposed action tool's execution. The server-side
 * tool handler is suspended awaiting this frame; on arrival the connection's
 * client-tool bridge settles the pending promise and the agent loop resumes,
 * feeding `result` (or `error`) back to the model as the tool result.
 *
 * Routed *out-of-band* on the server (like {@link IStopTurnRequest}), NOT
 * serialized on the per-socket turn queue: the live turn awaiting the tool
 * handler is itself on that queue, so chaining the result behind it would
 * deadlock. Carries `toolId` (echoes the invoke's id) so the bridge can match
 * it to the right pending call; a `toolId` with no matching pending entry — a
 * late result after a stop/timeout already settled the call — is a silent
 * no-op. Exactly one of `result` / `error` is meaningful: `error` set means
 * the browser flow failed or could not run; otherwise `result` (possibly
 * `undefined`) is the flow's structured outcome.
 */
export interface IClientToolResult {
  type: typeof CLIENT_MESSAGE_TYPE.ClientToolResult;
  toolId: string;
  result?: unknown;
  error?: string;
}

// Resetting the session is no longer a client→server frame: the user types the
// `/reset` command (an ordinary `user_message`), the server detects it and runs
// the archive-and-rebind, then pushes the `thread_reset` directive. There is no
// separate "clear chat" request.
export type ClientMessage = IUserMessageRequest | IAlertExpandRequest | IStopTurnRequest | IClientToolResult;

// ============================================
// Server → Client
// ============================================

export type AssistantStatus = 'thinking' | 'complete';

export interface IStatusUpdate {
  type: typeof SERVER_MESSAGE_TYPE.Status;
  status: AssistantStatus;
}

export interface IToolStarted {
  type: typeof SERVER_MESSAGE_TYPE.ToolStarted;
  toolId: string;
  /**
   * The model-supplied user-facing goal for this call — a short fragment
   * ("Looking for your recent transactions") rendered to the user while the
   * tool runs. Populated for ALL viewers (not gated): unlike `toolName` /
   * `toolInput`, the goal is the user-facing tool-activity surface. Absent only
   * when the call carried no goal (legacy/edge); the client renders nothing in
   * that case. Live-stream only — the persisted goal is re-derived from the
   * `toolCall` block on rehydration (see `SnapshotMessage` `tool_call`).
   */
  goal?: string;
  /**
   * Tool name and arguments. Populated only for viewers who pass the
   * `debug-assistant` gate — every other session receives just `toolId`, since
   * tool detail is no longer rendered in the UI and only feeds the debug
   * console. Live-stream only.
   */
  toolName?: string;
  toolInput?: Record<string, unknown>;
}

export interface IToolCompleted {
  type: typeof SERVER_MESSAGE_TYPE.ToolCompleted;
  toolId: string;
  /**
   * Tool name and full output. Populated only for viewers who pass the
   * `debug-assistant` gate — every other session receives just `toolId`, since
   * tool detail is no longer rendered in the UI and only feeds the debug
   * console. Live-stream only; not persisted, so it does not reappear on
   * transcript rehydration.
   */
  toolName?: string;
  result?: unknown;
  /**
   * What this call left a work item as — present only when the write itself
   * reported a real change against the row it read first, so an update that
   * restates the same state, errors, or matches no row carries none. Sent to
   * every viewer: the transcript draws the call's move line from it, and
   * arguments and output stay gated.
   */
  workItemMove?: IWorkItemMoveFacts;
  /** Bare moved-item id, kept while deployed clients still read only this. */
  workItemId?: string;
}

/**
 * One token-level slice of an in-flight assistant message — the text the
 * model just produced, NOT the running accumulation. Emitted as the model
 * streams prose within a round, ahead of that round's terminal
 * {@link IAssistantMessage}. Wire-only ephemera: never persisted.
 *
 * Carries no ids — like every frame here it relies on one-WS-per-thread +
 * in-order delivery + server-serialized turns. The client seeds a streaming
 * bubble on the first delta of a round and appends each subsequent delta;
 * the round's terminal {@link IAssistantMessage} snaps that bubble to the
 * authoritative full text. A round that streams any delta always emits that
 * terminal frame (a `text_delta` implies a non-empty text block), so the
 * streaming bubble is always finalized on the non-error path.
 */
export interface IAssistantMessageDelta {
  type: typeof SERVER_MESSAGE_TYPE.AssistantMessageDelta;
  text: string;
}

/**
 * Streamed mid-turn assistant text. Emitted once per LLM round whose
 * assistant message carried any non-empty text — so a multi-round turn
 * (the bootstrap directive, or any user turn where the model writes
 * prose between tool calls) yields multiple `assistant_message` frames,
 * each rendered as its own bubble.
 *
 * Carries the authoritative full text for the round and is the finalize
 * boundary for any {@link IAssistantMessageDelta} frames streamed ahead of
 * it. Does NOT carry `clientMessageIds`. The turn-end confirmation rides
 * on a separate {@link ITurnComplete} frame at the very end of the
 * turn so a single confirm pass flips every pending bubble at once.
 */
export interface IAssistantMessage {
  type: typeof SERVER_MESSAGE_TYPE.AssistantMessage;
  content: string;
}

/**
 * Terminal frame for one turn — emitted after the last
 * {@link IAssistantMessage} and any trailing `status: 'complete'`.
 * Carries the echo of every originating
 * {@link IUserMessageRequest}.messages[i].clientMessageId whose row
 * participated in this turn; the client flips each matching pending
 * bubble to confirmed in one pass.
 *
 * For server-initiated turns (today: the proactive session bootstrap)
 * `clientMessageIds` is absent — there are no pending bubbles to
 * confirm. The client still uses the frame as the signal to drain its
 * mid-turn typed-message queue.
 */
export interface ITurnComplete {
  type: typeof SERVER_MESSAGE_TYPE.TurnComplete;
  clientMessageIds?: string[];
  /**
   * The turn ended without leaving anything in the transcript, so a reload will
   * not reproduce it — the client must take back whatever it showed on the
   * turn's behalf. Set when a stop lands before the first round completes (the
   * directive row alone is dropped rather than persisted orphaned), when the
   * turn produced no text at all, and when the append itself fails.
   *
   * Absent means the ordinary case: the turn is in history and what is on
   * screen matches what rehydration will rebuild. It is stated only when false
   * so the common frame stays the shape it has always been.
   *
   * Says nothing about a channel that never persists — the claim is that a turn
   * expected to land did not, so an orchestrator running without a persistence
   * callback leaves it absent rather than reporting every turn discarded.
   */
  discarded?: boolean;
}

export interface IErrorMessage {
  type: typeof SERVER_MESSAGE_TYPE.Error;
  message: string;
  code?: string;
}

export interface ITranscriptSnapshot {
  type: typeof SERVER_MESSAGE_TYPE.TranscriptSnapshot;
  messages: SnapshotMessage[];
}

/**
 * Handshake message emitted once the server has finished rehydrating
 * the thread and built the orchestrator. Always sent immediately after
 * {@link ITranscriptSnapshot}. `user_message` frames received before this
 * signal are rejected with `ASSISTANT_NOT_READY` (the WS stays open; the client
 * retries once the handshake completes).
 */
export interface IAssistantReady {
  type: typeof SERVER_MESSAGE_TYPE.AssistantReady;
}

/**
 * View-reset directive. Sent at the start of a server-driven thread reset (a
 * typed `/reset` command) to tell the client to tear down its conversation view
 * (clearing every message, including the pending optimistic `/reset` bubble the
 * user just typed) and gate further sends until the next {@link IAssistantReady}.
 * The {@link ITranscriptSnapshot} + `assistant_ready` that follow rebind the
 * fresh thread; the snapshot carries the previous chained thread's rows, so the
 * user keeps seeing recent history even though the new thread is empty.
 */
export interface IThreadReset {
  type: typeof SERVER_MESSAGE_TYPE.ThreadReset;
}

/**
 * Server → client request to run a *client-exposed action tool* in the
 * browser — the first half of a round-trip whose second half is
 * {@link IClientToolResult}. Emitted when the model calls a tool whose handler
 * is a human-in-the-browser JS flow (today: `financial_account_connect`, which
 * drives the Plaid Link SDK) rather than a server computation. The server-side
 * tool handler suspends until the matching `client_tool_result` arrives.
 *
 * Only frames the client has a handler for are ever sent — these tools are
 * server-gated to the app/WebSocket channel (SMS/email carry no JS runtime and
 * never see this frame; there the assistant points the user to the web app
 * instead). `params` is the model-supplied tool input with the audit params
 * (`goal` / `justification`) already stripped. No user-facing label rides here:
 * `toolId` matches the {@link IToolStarted} frame emitted for the same call, so
 * the client renders the activity line ("Connecting your account…") off that
 * frame's `goal` and uses this one purely to launch the flow.
 */
export interface IInvokeClientTool {
  type: typeof SERVER_MESSAGE_TYPE.InvokeClientTool;
  toolId: string;
  toolName: string;
  params: Record<string, unknown>;
}

/**
 * Server → client directive to abandon an in-flight {@link IInvokeClientTool}
 * whose server-side handler is no longer waiting for it — the user stopped the
 * turn, or the bridge's round-trip timeout elapsed. The client tears down the
 * open flow (e.g. closes the Plaid modal). Advisory and idempotent: a
 * `client_tool_result` the browser sends anyway (it completed the flow before
 * receiving the cancel) lands on a no-longer-pending `toolId` and is ignored
 * server-side.
 */
export interface ICancelClientTool {
  type: typeof SERVER_MESSAGE_TYPE.CancelClientTool;
  toolId: string;
}

/**
 * Server → client "an assistant write changed your data — refresh" signal.
 * Emitted (app channel only) after a non-read tool completes successfully, so
 * the rest of the app re-reads instead of showing data the assistant has
 * already mutated through its own channel. Payload-free by design: the client
 * responds by refetching its active GraphQL queries wholesale rather than
 * targeting a typename, so there is nothing finer to carry yet. Live-stream
 * ephemera — never persisted, so it does not reappear on transcript rehydration.
 */
export interface IDataChanged {
  type: typeof SERVER_MESSAGE_TYPE.DataChanged;
}

/**
 * Server → client "this turn is about that alert" frame, emitted once at the
 * head of an alert-expand turn so the card renders as the reply starts rather
 * than after it lands.
 *
 * **Optimistic.** It goes out before the turn runs, and therefore before the
 * `system_instruction` row it corresponds to is persisted — so unlike the
 * `alert_card` snapshot row it is not yet a claim about history. A turn that
 * does not reach the transcript takes this card back along with everything else
 * it put up, and that retraction — not the emission point — is what keeps live
 * and rehydrated equal.
 *
 * Two different frames deliver it, which matters to anyone writing the client
 * half: a turn that *completes* without landing carries `discarded` on
 * `turn_complete`, but a turn that **errors** never reaches one — it throws,
 * and an `error` frame arrives instead. Both end the turn client-side, so both
 * must retract; keying the retraction on `discarded` alone would strand the
 * card on precisely the failure most likely to be seen.
 *
 * Still server-emitted rather than appended client-side on click, so the id the
 * card renders is one the server resolved against the viewer's own alerts.
 *
 * Carries `alertId` — the client reads the live callout for everything it
 * renders. See the `alert_card` variant of `SnapshotMessage`.
 */
export interface IAlertCard {
  type: typeof SERVER_MESSAGE_TYPE.AlertCard;
  alertId: string;
}

export type ServerMessage =
  | IStatusUpdate
  | IToolStarted
  | IToolCompleted
  | IAssistantMessageDelta
  | IAssistantMessage
  | ITurnComplete
  | IErrorMessage
  | ITranscriptSnapshot
  | IAssistantReady
  | IThreadReset
  | IInvokeClientTool
  | ICancelClientTool
  | IDataChanged
  | IAlertCard;

// Re-export the snapshot row type used by ITranscriptSnapshot so
// every wire-protocol consumer can pull from one path.
export type { IObservationDocument, SnapshotMessage };
